import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { useCurrentOrgId } from '@/hooks/useCurrentOrgId'
import { Button } from '@/components/ui'
import type { InvoiceRecipientResponse } from '@spoofio/shared/types'

export function InvoiceRecipients() {
  const { orgId } = useCurrentOrgId()
  const queryClient = useQueryClient()
  const [newEmail, setNewEmail] = useState('')
  const [addError, setAddError] = useState<string | null>(null)

  const { data: recipients, isLoading } = useQuery({
    queryKey: ['invoice-recipients', orgId],
    queryFn: () => api.listInvoiceRecipients(orgId ?? ''),
    enabled: !!orgId,
  })

  const addRecipient = useMutation({
    mutationFn: (email: string) => api.addInvoiceRecipient(orgId ?? '', { email }),
    onSuccess: () => {
      setNewEmail('')
      setAddError(null)
      void queryClient.invalidateQueries({ queryKey: ['invoice-recipients'] })
    },
    onError: () => {
      setAddError('Failed to add recipient. The email may already exist.')
    },
  })

  const removeRecipient = useMutation({
    mutationFn: (id: string) => api.removeInvoiceRecipient(orgId ?? '', id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice-recipients'] })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = newEmail.trim()
    if (!trimmed) return
    setAddError(null)
    addRecipient.mutate(trimmed)
  }

  return (
    <div className="card">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Invoice Recipients
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Email addresses that receive monthly billing invoices.
        </p>
      </div>
      <div className="px-4 py-5 sm:p-6">
        {/* Add recipient form */}
        <form onSubmit={handleSubmit} className="flex gap-2 mb-4">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => { setNewEmail(e.target.value) }}
            placeholder="billing@example.com"
            className="input flex-1"
            required
          />
          <Button
            type="submit"
            size="sm"
            isLoading={addRecipient.isPending}
          >
            Add
          </Button>
        </form>
        {addError && (
          <p className="text-xs text-danger-600 mb-3">{addError}</p>
        )}

        {/* Recipient list */}
        {isLoading && (
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-gray-100 rounded" />
            <div className="h-8 bg-gray-100 rounded" />
          </div>
        )}
        {recipients?.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-2">
            No invoice recipients configured.
          </p>
        )}
        {recipients && recipients.length > 0 && (
          <ul className="divide-y divide-gray-200">
            {recipients.map((r: InvoiceRecipientResponse) => (
              <li key={r.id} className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-900">{r.email}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={removeRecipient.isPending}
                  onClick={() => { removeRecipient.mutate(r.id) }}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
